import * as React from 'react';
import { FileText, Loader2, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { CanvasDraft, CanvasDraftKind } from '@hector/shared-types';

const KIND_LABEL: Record<CanvasDraftKind, string> = {
  generated: 'Généré',
  imported: 'Importé',
  blank: 'Vierge',
};

/** Date relative FR (« il y a 2 heures »). Best-effort : une date illisible ne casse pas la ligne. */
function relativeDate(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: fr });
  } catch {
    return '';
  }
}

interface CanvasDraftsListProps {
  drafts: CanvasDraft[];
  isLoading: boolean;
  onSelect: (draft: CanvasDraft) => void;
  /** Phrase affichée quand la liste est vide — elle diffère selon la liste servie. */
  videLibelle?: string;
  /**
   * Le document actuellement ouvert, s'il est dans cette liste.
   *
   * Il reste VISIBLE — le retirer ferait croire à l'avocat qu'il a disparu — mais
   * n'est ni cliquable (il est déjà là) ni supprimable (on ne détruit pas le
   * document qu'on regarde ; il faut d'abord en ouvrir un autre).
   */
  draftOuvertId?: string | null;
  /**
   * Absent = liste en lecture seule, sans corbeille.
   *
   * ⚠️ N'est appelé qu'après CONFIRMATION (2026-09-23). La suppression emporte
   * la ligne, le `.docx` et le journal du document, sans corbeille serveur ni
   * annulation, et la corbeille est une cible de quelques pixels dans un menu
   * déroulant : un clic à côté de la ligne visée détruisait un acte entier.
   */
  onDelete?: (draft: CanvasDraft) => void;
}

/**
 * Liste plate, présentationnelle, de brouillons canvas — rendue inline dans le
 * dropdown « Ouvrir ».
 *
 * Elle sert les DEUX listes du menu, qui ne font pourtant pas la même chose :
 * « Documents de cette conversation » (on y bascule, en adoptant l'identité du
 * brouillon) et « Copier un brouillon d’une autre conversation », transverse au cabinet (on en
 * copie le contenu). La différence vit chez l'appelant, jamais ici : aucune
 * donnée n'est fetchée dans ce composant.
 */
export function CanvasDraftsList({
  drafts, isLoading, onSelect, videLibelle = 'Aucun brouillon', draftOuvertId, onDelete,
}: CanvasDraftsListProps): React.JSX.Element {
  // Une confirmation EN LIGNE plutôt qu'une boîte de dialogue : la liste vit
  // dans un menu déroulant Radix, qu'une modale ouverte par-dessus refermerait
  // en lui volant le focus.
  const [aConfirmer, setAConfirmer] = React.useState<string | null>(null);
  if (isLoading) {
    return (
      <div className="px-2 py-3 flex items-center gap-2 text-xs text-stone-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement…
      </div>
    );
  }
  if (drafts.length === 0) {
    return <div className="px-2 py-3 text-xs text-stone-500">{videLibelle}</div>;
  }
  return (
    <div className="max-h-64 overflow-y-auto">
      {drafts.map((draft) => {
        const ouvert = draft.id === draftOuvertId;
        if (onDelete && aConfirmer === draft.id) {
          return (
            <div
              key={draft.id}
              role="group"
              aria-label={`Confirmer la suppression de ${draft.title}`}
              className="flex items-center gap-1 rounded-md bg-red-50 px-2 py-1.5 text-[13px]"
            >
              <span className="flex-1 min-w-0 truncate text-red-800">
                Supprimer définitivement « {draft.title} » ?
              </span>
              <button
                type="button"
                onClick={() => { setAConfirmer(null); onDelete(draft); }}
                className="shrink-0 rounded-md bg-red-600 px-2 py-0.5 text-[12px] font-medium text-white hover:bg-red-700"
              >
                Supprimer
              </button>
              <button
                type="button"
                onClick={() => setAConfirmer(null)}
                className="shrink-0 rounded-md px-2 py-0.5 text-[12px] text-stone-600 hover:bg-stone-100"
              >
                Annuler
              </button>
            </div>
          );
        }
        return (
          <div key={draft.id} className="flex items-center gap-1 rounded-md hover:bg-stone-100">
            <button
              type="button"
              onClick={() => { if (!ouvert) onSelect(draft); }}
              // `aria-disabled` et non `disabled` : un bouton désactivé ne reçoit
              // aucun événement de pointeur, donc plus d'infobulle ni de nom lu —
              // la doctrine du bandeau (#853) vaut aussi ici.
              aria-disabled={ouvert || undefined}
              title={ouvert ? 'Document actuellement ouvert' : undefined}
              className={`flex-1 min-w-0 px-2 py-1.5 flex items-center gap-2 text-left text-[13px] ${ouvert ? 'cursor-default' : ''}`}
            >
              <FileText className={`h-3.5 w-3.5 shrink-0 ${ouvert ? 'text-stone-700' : 'text-stone-500'}`} />
              <span className={`flex-1 min-w-0 truncate ${ouvert ? 'font-medium text-stone-900' : ''}`}>{draft.title}</span>
              {ouvert && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-800 text-white shrink-0">Ouvert</span>
              )}
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600 border border-stone-200 shrink-0">
                {KIND_LABEL[draft.kind]}
              </span>
              <span className="text-[11px] text-stone-400 shrink-0">{relativeDate(draft.updatedAt)}</span>
            </button>
            {onDelete && (
              <button
                type="button"
                onClick={() => { if (!ouvert) setAConfirmer(draft.id); }}
                aria-disabled={ouvert || undefined}
                aria-label={`Supprimer ${draft.title}`}
                title={ouvert
                  ? 'Ouvrez un autre document avant de supprimer celui-ci.'
                  : 'Supprimer ce document'}
                className={`shrink-0 p-1.5 rounded-md ${ouvert ? 'text-stone-300 cursor-default' : 'text-stone-400 hover:text-red-600 hover:bg-red-50'}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
